import { ReactNode, useCallback, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { useDropzone } from 'react-dropzone';
import { useQueryClient } from 'react-query';
import { DepartmentsApi } from '@frontend/api-departments';
import { PhoneMediaApi, PhoneMediaTypes } from '@frontend/api-phone-media';
import { VoiceMailboxTypes } from '@frontend/api-voicemail-boxes';
import { getInitialParams } from '@frontend/env';
import { useTranslation } from '@frontend/i18n';
import { useMutation } from '@frontend/react-query-helpers';
import { useScopedQuery } from '@frontend/scope';
import { theme } from '@frontend/theme';
import {
  BackIcon,
  ButtonBar,
  CellPhoneIcon,
  FieldChangeEvent,
  Heading,
  Modal,
  ModalControlModalProps,
  PhoneIcon,
  PrimaryButton,
  SecondaryButton,
  SpinningLoader,
  Text,
  TextField,
  UploadIcon,
  useFormField,
  useModalControl,
  useAlert,
} from '@frontend/design-system';
import { queryKeys } from '../query-keys';
import { logError } from '../utils/loggers';

// This type is just for handling the media types within the form field
type MediaFile = (PhoneMediaTypes.HoldMusicFile | PhoneMediaTypes.PhoneMedia | VoiceMailboxTypes.Greeting) & {
  isGlobal: boolean;
  isVMGreeting?: boolean;
  selectedId: string;
  FilePath: string;
};

export enum MediaPickerOptionValues {
  DEFAULT_GREETING = 'default',
  NO_GREETING = 'none',
}

export type MediaPickerFileState = MediaFile | 'none' | 'default' | undefined;

/** This is the path we will use for default greetings */
// const DEFAULT_GREETING_PATH = getInitialParams().backendApi?.includes('api.weavedev.net')
//   ? 'https://api.weavedev.net/portal/v1/phonemedia/305a7cd9-21ae-4430-af05-1d2a9a24dc6a/download'
//   : 'https://api.gke1-west3.wsf-prod-1.wstack.net/portal/v1/phonemedia/3144d7d9-e84a-45e1-b94d-a4e209990899/download';

export const DEFAULT_STANDARD_MEDIA = getInitialParams().backendApi?.includes('api.weavedev.net')
  ? '1b6d7018-bd1d-4b05-bede-499ec4d8c205'
  : '021bceb8-0d4b-4061-b3a6-cec962a0c085';

export const getFileExtensionFromFileName = (fileName: string) => {
  if (!fileName) return '';

  const dotIndex = fileName.lastIndexOf('.');

  if (dotIndex < 0 || dotIndex === fileName.length - 1) {
    return '';
  }

  return fileName.substring(dotIndex + 1);
};

interface IAudioFileDropzone {
  onChange: (file: AudioFile) => void;
}

export const MediaFileDropzone = ({ onChange }: IAudioFileDropzone) => {
  const { t } = useTranslation('phone', { keyPrefix: 'media' });

  const [fileName, setFileName] = useState('');
  const [error, setError] = useState('');

  const onDrop = useCallback((acceptedFiles: any, rejectedFiles: any) => {
    if (acceptedFiles.length > 0) {
      const file: AudioFile = acceptedFiles[0];
      setError('');
      setFileName(file.name);
      onChange(file);
    } else if (rejectedFiles.length > 0) {
      setError(t('Invalid file type: Must be WAV, MPEG (MP3) or MP4'));
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { 'audio/*': ['.wav', '.mpeg', '.m4a'], 'video/*': ['.mp4'] },
    onDrop,
  });

  const onBrowseClick = useMemo(() => getRootProps().onClick, [getRootProps]);

  let content;
  if (isDragActive) {
    content = <Text>{t('Drop file here...')}</Text>;
  } else if (fileName) {
    content = (
      <span>
        {t('File: ')}
        <Text as='span' weight='bold'>
          {fileName}
        </Text>
      </span>
    );
  } else {
    content = (
      <div
        css={[
          css`
            height: 100%;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            gap: ${theme.spacing(1)};
          `,
        ]}
      >
        <BackIcon
          size={20}
          css={css`
            transform: rotate(90deg);
            color: ${theme.colors.neutral30};
          `}
        />
        <Text size='medium' as='span'>
          {t('Drop WAV, MPEG (MP3), or MP4 file here')}
        </Text>
        <SecondaryButton style={{ width: 'fit-content' }} onClick={onBrowseClick}>
          {t('Choose File')}
        </SecondaryButton>
      </div>
    );
  }

  return (
    <div
      css={css`
        background-color: ${theme.colors.neutral5};
        border: 1px dashed ${theme.colors.neutral30};
        border-radius: ${theme.borderRadius.small};
        display: flex;
        justify-content: center;
        align-items: center;
        width: ${theme.spacing(58)};
        height: ${theme.spacing(16)};
        margin-top: ${fileName ? theme.spacing(3) : theme.spacing(2)};
      `}
      {...getRootProps({
        onClick: (event) => {
          if (fileName) {
            return;
          }
          event.stopPropagation();
        },
      })}
    >
      <input {...getInputProps()} />
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}
      >
        {content}
        {error && (
          <div
            css={css`
              color: ${theme.colors.status.critical};
              margin-top: ${theme.spacing(2)};
            `}
          >
            {error}
          </div>
        )}
      </div>
    </div>
  );
};

interface Props {
  locationId: string;
  file: AudioFile;
  mediaFieldName?: string;
  onSettled: () => void;
  onSuccessfulUpload?: (mediaID: string) => void;
  onPhoneMediaSelected?: (mediaId: string) => void;
  onMediaChange?: (e?: FieldChangeEvent, selectedMedia?: MediaPickerFileState) => void;
}

type AudioFile = File;

const AddMediaUploadForm = ({
  locationId,
  file,
  mediaFieldName,
  onSettled,
  onSuccessfulUpload,
  onPhoneMediaSelected,
  onMediaChange,
}: Props) => {
  const alerts = useAlert();
  const queryClient = useQueryClient();
  const { t } = useTranslation('phone', { keyPrefix: 'media' });
  const [isSaving, setIsSaving] = useState(false);
  const nameFieldProps = useFormField(
    {
      type: 'text',
      required: true,
      // Assumes that the file has an extension
      value: file.name.slice(0, file.name.lastIndexOf('.')),
    },
    [file.name]
  );

  const mutation = useMutation(
    ({ file, fileName }: { file: AudioFile; fileName: string }) => {
      return PhoneMediaApi.upload(file, fileName, { locationId });
    },
    {
      onSuccess: (data: PhoneMediaTypes.UploadMediaResponse) => {
        // Invalidate queries first to ensure backend readiness
        queryClient
          .invalidateQueries([locationId, ...queryKeys.phoneMedia()])
          .then(() => {
            if (data?.ID) {
              onSuccessfulUpload?.(data.ID);
              onPhoneMediaSelected?.(data.ID ?? '');
              onMediaChange?.({ name: mediaFieldName || 'greetingMedia', value: data.ID });
            } else {
              console.warn('No media ID received in response');
            }
          })
          .finally(() => {
            onSettled();
          });
      },
      onError: () => {
        alerts.error(t('Failed to upload media file. Please refresh the page and try again.'));
        logError('AddMediaUploadForm:uploadMutation | Failed to upload media file', {
          data: { locationId, fileName: file.name },
        });
        onSettled();
      },
    }
  );

  const uploadPhoneMedia = useCallback(() => {
    if (file) {
      setIsSaving(true);
      const ext = getFileExtensionFromFileName(file.name);
      mutation.mutate({ file: file, fileName: `${nameFieldProps.value}.${ext}` });
    }
  }, [file, nameFieldProps.value]);

  return (
    <div>
      <div
        css={css`
          margin-top: ${theme.spacing(2)};
          text-align: left;
          width: ${theme.spacing(58)};
        `}
      >
        <TextField
          {...nameFieldProps}
          name='phoneMediaName'
          label={t('File Name')}
          helperText={t('Give your file a name you can remember')}
        />
      </div>

      <ButtonBar
        css={css`
          justify-content: center;
          padding: 0;
          margin-top: ${theme.spacing(3)};
        `}
      >
        <SecondaryButton onClick={onSettled} style={{ width: 'fit-content' }}>
          Cancel
        </SecondaryButton>
        <PrimaryButton onClick={uploadPhoneMedia} style={{ width: 'fit-content' }} disabled={isSaving}>
          {/* This shows a spinning loader when we save */}
          {isSaving ? (
            <SpinningLoader
              css={{
                div: {
                  borderColor: 'white transparent transparent transparent',
                },
              }}
              size='small'
            />
          ) : (
            t('Upload File')
          )}
        </PrimaryButton>
      </ButtonBar>
    </div>
  );
};

const Section = ({ children }: { children: ReactNode }) => {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: theme.spacing(3),
        width: '100%',
      }}
    >
      {children}
    </div>
  );
};

export const UploadDescription = () => {
  const { t } = useTranslation('phone', { keyPrefix: 'media' });
  const { data: departments = [] } = useScopedQuery({
    queryKey: queryKeys.listDepartments(),
    queryFn: () => DepartmentsApi.listDept({}),
    select: (data) => data.departments,
  });
  const isNonDeptOffice = departments?.length === 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2), padding: theme.spacing(0, 2) }}>
      <Section>
        <PhoneIcon />
        <div>
          <Heading level={2} textAlign='left'>
            {t('Record an audio file from your phone')}
          </Heading>
          <Text
            textAlign='left'
            css={css`
              line-height: ${theme.spacing(2.5)};
            `}
          >
            {t(
              'To record a message using your Weave desk phone, dial *86 and record your message following the prompt. Refresh the page to see your new file appear.'
            )}
          </Text>
        </div>
      </Section>
      <Section>
        <CellPhoneIcon />
        <div>
          <Heading level={2} textAlign='left'>
            {t('Record an audio file from the mobile app')}
          </Heading>
          <Text
            textAlign='left'
            css={css`
              line-height: ${theme.spacing(2.5)};
            `}
          >
            {isNonDeptOffice
              ? t(
                  `To record a message using your mobile app, open the app and go into Media Library tapping Settings > Phones > Media Library then choose "Record New."`
                )
              : t(
                  `To record a message using your mobile app, open the app and go into Media Library tapping Settings > Phones > Main Line > Media Library then choose "Record New."`
                )}
          </Text>
        </div>
      </Section>
      <Section
        css={css`
          margin-bottom: 40px;
        `}
      >
        <UploadIcon />
        <Heading level={2} textAlign='left'>
          {t('Upload an existing audio file')}
        </Heading>
      </Section>
    </div>
  );
};

interface MediaUploadModalProps extends ModalControlModalProps {
  locationId: string;
  onSuccess?: (mediaID: string) => void;
  onMediaChange?: (e?: FieldChangeEvent, selectedMedia?: MediaPickerFileState) => void;
  mediaFieldName?: string;
}

const MediaUploadModal = ({
  locationId,
  onSuccess,
  onMediaChange,
  mediaFieldName,
  ...modalProps
}: MediaUploadModalProps) => {
  const { t } = useTranslation('phone', { keyPrefix: 'media' });

  const [mediaFile, setMediaFile] = useState<AudioFile>();

  const onSettled = () => {
    // This is for aesthetics - so that the modal doesn't rerender while closing
    setTimeout(() => {
      setMediaFile(undefined);
    }, 500);
    modalProps.onClose();
  };

  const onChange = (file: AudioFile) => {
    if (file) {
      setMediaFile(file);
    }
  };

  return (
    <Modal
      {...modalProps}
      onClose={onSettled}
      css={css`
        min-width: ${mediaFile ? '513px' : '568px'};
        padding: ${theme.spacing(3, 0)};
        text-align: center;
      `}
    >
      <Modal.Header
        css={css`
          margin: 0;
          font-size: ${theme.fontSize(24)};
        `}
      >
        {mediaFile ? t('Name and Upload') : t('Add Audio File')}
      </Modal.Header>
      <Modal.Body>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: theme.spacing(2),
            padding: theme.spacing(2, 0),
          }}
        >
          {!mediaFile && <UploadDescription />}
          <MediaFileDropzone onChange={onChange} />
          {!!mediaFile && (
            <AddMediaUploadForm
              locationId={locationId}
              file={mediaFile}
              mediaFieldName={mediaFieldName}
              onSettled={onSettled}
              onSuccessfulUpload={onSuccess}
              onMediaChange={onMediaChange}
            />
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export const useMediaUploadModal = () => {
  const { triggerProps, modalProps } = useModalControl();

  return { triggerProps, modalProps, MediaUploadModal };
};
